import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';

import DocumentPageRenderer, { BoundingBoxData } from './DocumentPageRenderer';

interface MultiPageEvidenceViewerProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
  evidencePages: number[];
  boundingBoxes?: BoundingBoxData;
  measurementFound?: string;
  measurementLocation?: string;
  requirementTitle?: string;
  complianceStatus?: string;
  onPageClick?: (page: number) => void;
}

const MultiPageEvidenceViewer: React.FC<MultiPageEvidenceViewerProps> = ({
  open,
  onClose,
  documentId,
  documentName,
  evidencePages,
  boundingBoxes,
  measurementFound,
  measurementLocation,
  requirementTitle,
  complianceStatus,
  onPageClick
}) => {
  const [selectedPage, setSelectedPage] = useState<number | null>(null);

  // Sort pages for consistent display
  const sortedPages = [...evidencePages].sort((a, b) => a - b);

  const getStatusColor = () => {
    switch (complianceStatus) {
      case 'compliant':
        return 'success';
      case 'non_compliant':
        return 'error';
      case 'not_applicable':
        return 'info';
      default:
        return 'warning';
    }
  };

  const getStatusIcon = () => {
    switch (complianceStatus) {
      case 'compliant':
        return '✓';
      case 'non_compliant':
        return '✗';
      case 'not_applicable':
        return 'N/A';
      default:
        return '?';
    }
  };

  const handlePageClick = (page: number) => {
    setSelectedPage(page);
    if (onPageClick) {
      onPageClick(page);
    }
  };

  const getEvidenceCountForPage = (page: number): number => {
    const pageKey = `page_${page}`;
    const pageBoxes = boundingBoxes?.[pageKey];
    return Array.isArray(pageBoxes) ? pageBoxes.length : 0;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: '85vh',
          maxHeight: '95vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1
        }}
      >
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            All Evidence Pages
          </Typography>
          {requirementTitle && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
              {requirementTitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* Document Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            📄 {documentName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {sortedPages.length} evidence page{sortedPages.length !== 1 ? 's' : ''}
            </Typography>
            {complianceStatus && (
              <Chip
                label={`${getStatusIcon()} ${complianceStatus.toUpperCase().replace('_', ' ')}`}
                color={getStatusColor() as any}
                size="small"
                variant="filled"
              />
            )}
          </Box>
        </Box>

        {/* Evidence Context */}
        {(measurementFound || measurementLocation) && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {measurementFound && (
              <Typography variant="body2" sx={{ mb: measurementLocation ? 1 : 0 }}>
                <strong>Measurement Found:</strong> {measurementFound}
              </Typography>
            )}
            {measurementLocation && (
              <Typography variant="body2">
                <strong>Location:</strong> {measurementLocation}
              </Typography>
            )}
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Page Grid */}
        <Grid container spacing={3}>
          {sortedPages.map(page => {
            const evidenceCount = getEvidenceCountForPage(page);
            const isSelected = selectedPage === page;

            return (
              <Grid item xs={12} sm={6} md={4} key={page}>
                <Card
                  elevation={isSelected ? 3 : 1}
                  sx={{
                    border: isSelected ? 2 : 1,
                    borderColor: isSelected ? 'primary.main' : 'grey.300',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      elevation: 2,
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <CardActionArea onClick={() => handlePageClick(page)}>
                    <CardContent sx={{ p: 2 }}>
                      {/* Page Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Page {page}
                        </Typography>
                        {evidenceCount > 0 && (
                          <Chip
                            label={`${evidenceCount} evidence area${evidenceCount !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {/* Page Preview */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          backgroundColor: 'grey.50',
                          borderRadius: 1,
                          p: 1,
                          minHeight: 200
                        }}
                      >
                        <DocumentPageRenderer
                          documentId={documentId}
                          pageNumber={page}
                          boundingBoxes={boundingBoxes}
                          width={300}
                        />
                      </Box>

                      {/* Click to expand indicator */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                          size="small"
                          startIcon={<FullscreenIcon />}
                          sx={{ textTransform: 'none' }}
                        >
                          Click to view full size
                        </Button>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          Click any page to view in detail
        </Typography>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiPageEvidenceViewer;